import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';

import { Configuration } from '@shared/models/configuration';
import {
  ITranscriptionService,
  TranscriptionResult,
  TranscriptionStartOptions,
  TranscriptionTokenResponse,
} from './transcription.service';

/**
 * Deepgram concrete implementation of {@link ITranscriptionService}.
 *
 * Flow:
 *   1. Ask the backend (`POST /api/bot-agent/transcription-token`) for a
 *      short-TTL Deepgram access token. The long-lived key never leaves the server.
 *   2. Open a WebSocket to `wss://api.deepgram.com/v1/listen` with the
 *      token carried in `Sec-WebSocket-Protocol: ['bearer', <token>]` (browsers
 *      can't set arbitrary HTTP headers on WS upgrades, so Deepgram
 *      documents this sub-protocol channel for the auth credential).
 *   3. Request the user's microphone via `getUserMedia` with browser AEC /
 *      noise suppression / AGC enabled — handles the bot-TTS echo loop in
 *      most cases without extra work.
 *   4. Pipe the mic into a `MediaRecorder` set to `audio/webm;codecs=opus`
 *      and forward every 250ms chunk over the WebSocket as a binary frame.
 *      We tell Deepgram `encoding=opus` so it interprets the bytes directly.
 *   5. Parse Deepgram's `Results` JSON frames; emit a TranscriptionResult
 *      with `isFinal = is_final && speech_final` so the host only sees a
 *      single "done" signal per utterance.
 *
 * Pause vs Stop:
 *   - `pause()` flips a flag that drops outgoing binary frames. The socket
 *     stays open so we resume instantly when the bot finishes speaking.
 *   - `stop()` tears everything down: closes the WS, stops the recorder,
 *     releases the mic.
 */
@Injectable({ providedIn: 'root' })
export class DeepgramTranscriptionService extends ITranscriptionService {
  result$: Observable<TranscriptionResult>;
  connectionLost$: Observable<void>;
  isRunning = false;

  private socket: WebSocket | null = null;
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private paused = false;
  private resultSubject = new Subject<TranscriptionResult>();
  private connectionLostSubject = new Subject<void>();

  /**
   * True only while stop() itself is tearing things down — distinguishes a deliberate close from
   * an unexpected one so the WebSocket 'close' handler knows whether to reconnect.
   */
  private stopRequested = false;

  /** Backoff state for reconnecting after an unexpected close. Reset to 0 on a successful start(). */
  private reconnectAttempts = 0;
  private reconnectTimer: any = null;
  private static readonly MAX_RECONNECT_ATTEMPTS = 3;
  private static readonly RECONNECT_BASE_DELAY_MS = 1000;

  /**
   * Periodic KeepAlive timer. Deepgram closes the WS (code 1011) if it
   * receives no data for ~10s. The echo-guard pause() stops binary audio
   * frames while the bot speaks via TTS — which can easily exceed 10s on
   * a long answer. Sending `{"type":"KeepAlive"}` every 8s resets
   * Deepgram's idle timer without incurring transcription cost.
   *
   * See https://developers.deepgram.com/reference/streaming
   */
  private keepAliveHandle: any = null;
  private readonly KEEPALIVE_INTERVAL_MS = 8000;

  /**
   * Accumulator for `is_final` segments that haven't been committed yet.
   * Deepgram emits a sequence of is_final=true segments followed (eventually)
   * by either a Results frame with `speech_final=true` OR a separate
   * `UtteranceEnd` event when its VAD detects the user has stopped speaking.
   * We commit on whichever arrives first — committing only on `speech_final`
   * means utterances that lack trailing silence get stuck in interim forever.
   */
  private utteranceBuffer = '';
  /** MEET-E-13-01: the confidences of the segments in `utteranceBuffer`, averaged onto the final. */
  private utteranceConfidences: number[] = [];

  /** MEET-E-27-02: resolves the pending finish() wait once Deepgram answers the `Finalize`. */
  private finalizeDone: (() => void) | null = null;
  /** MEET-E-27-02: the longest finish() waits for Deepgram's flushed final before committing what it has. */
  static readonly FINALIZE_WAIT_MS = 1500;

  /** How the current stream was started — a reconnect starts the same way. */
  private startOptions: TranscriptionStartOptions = {};

  constructor(private http: HttpClient) {
    super();
    this.result$ = this.resultSubject.asObservable();
    this.connectionLost$ = this.connectionLostSubject.asObservable();
  }

  async start(options?: TranscriptionStartOptions): Promise<void> {
    if (this.isRunning) return;
    this.stopRequested = false;
    if (options) this.startOptions = {...options};
    const meetId = this.startOptions.meetId;

    // 1) Mint a short-TTL key on the backend. Falls through to a throw if
    //    the user lacks the feature or the provider config is missing —
    //    the host catches and surfaces a friendly error.
    //
    // NOTE: Configuration.XatBotApi already resolves to the API root
    // INCLUDING `/api` (e.g. http://localhost:50544/api). Do NOT prepend
    // /api/ here or the URL becomes http://.../api/api/... → 404.
    const token = await firstValueFrom(
      this.http.post<TranscriptionTokenResponse>(
        `${this.apiBase()}/bot-agent/transcription-token`
          + (meetId ? `?meetId=${encodeURIComponent(meetId)}` : ''),
        {},
      ),
    );

    if (!token?.token || !token?.wsBaseUrl) {
      throw new Error('[DeepgramTranscription] token response missing token / wsBaseUrl');
    }

    if (token.provider !== 'Deepgram') {
      // The dispatcher selected a non-Deepgram provider but the module is
      // wired to the Deepgram concrete. Bail loudly so the swap-provider
      // mistake is obvious in DevTools rather than failing silently mid-call.
      throw new Error(`[DeepgramTranscription] backend returned provider='${token.provider}', expected 'Deepgram'.`);
    }

    // 2) Build the Deepgram /v1/listen query string. Mirrors the meeting
    //    flow's nova-3 + punctuate + interim_results config. We additionally
    //    enable smart_format (chat-thread-friendly text), endpointing (300ms
    //    silence = utterance end), and vad_events (for future barge-in).
    const params = new URLSearchParams({
      model:           token.model    || 'nova-3',
      language:        token.language || 'en-US',
      punctuate:       'true',
      interim_results: 'true',
      smart_format:    'true',
      endpointing:     '300',
      vad_events:      'true',
      // `utterance_end_ms` is REQUIRED for Deepgram to actually emit
      // `UtteranceEnd` events. Without it, we only get `SpeechStarted` from
      // vad_events — and our commit pipeline depends on `UtteranceEnd` as
      // the fallback when `speech_final` never fires (which is common when
      // the user trails off without a clean silence window). 1000ms is the
      // documented minimum; raise if the bot is committing too aggressively.
      utterance_end_ms: '1000',
      encoding:        'opus',
    });
    (token.keyterms ?? []).forEach(k => {
      if (k && k.trim().length > 0) params.append('keyterm', k.trim());
    });

    // Sub-protocol auth — `['bearer', token]`. The first element is the
    // protocol name Deepgram registered for a granted access token; the
    // second is the token itself.
    this.socket = new WebSocket(
      `${token.wsBaseUrl}?${params.toString()}`,
      ['bearer', token.token],
    );
    this.socket.binaryType = 'arraybuffer';

    this.socket.addEventListener('message', evt => this.handleMessage(evt));
    this.socket.addEventListener('error', evt => {
      console.warn('[DeepgramTranscription] WebSocket error', evt);
    });
    this.socket.addEventListener('close', evt => {
      console.log('[DeepgramTranscription] WebSocket closed', evt.code, evt.reason);
      this.isRunning = false;
      this.finalizeDone?.(); // a finish() waiting on this socket has nothing more coming
      // Stop the KeepAlive timer so we don't fire into a dead socket.
      if (this.keepAliveHandle) {
        clearInterval(this.keepAliveHandle);
        this.keepAliveHandle = null;
      }
      // A close we didn't ask for (network blip, Deepgram-side close, proxy reset, ...) used to
      // just leave capture dead for the rest of the show with no recovery and no feedback — the
      // 'error'/'close' handlers only ever logged. Reconnect automatically instead.
      if (!this.stopRequested) {
        this.scheduleReconnect();
      }
    });

    await this.waitForSocketOpen(this.socket);

    // 3) Open the mic with browser-side echo cancellation + noise
    //    suppression + AGC. These get us most of the way against the
    //    bot-TTS-into-mic loop; pause() handles the rest.
    const deviceId = this.startOptions.deviceId;
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(deviceId && deviceId !== 'default' ? {deviceId: {exact: deviceId}} : {}),
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl:  true,
        },
      });
    } catch (err) {
      // XAT-E-608-07: mic denied or absent — close the socket we just opened (it would otherwise idle open and
      // reconnect on its own), and rethrow the DOMException as is so the host can tell NotAllowedError from NotFoundError.
      this.stopRequested = true;
      this.releaseMediaAndSocket();
      throw err;
    }

    // XAT-E-608-07: the audio stream itself dropped (mic unplugged, permission revoked mid-call). Reconnecting the
    // socket cannot bring it back, so the host is told at once.
    const stream = this.mediaStream;
    stream.getAudioTracks().forEach(track => track.addEventListener('ended', () => {
      if (this.stopRequested || this.mediaStream !== stream) return;
      console.warn('[DeepgramTranscription] microphone track ended');
      this.stop();
      this.connectionLostSubject.next();
    }));

    // 4) Start recording in webm/opus chunks. 250ms cadence is a good
    //    balance between latency and per-frame overhead.
    this.mediaRecorder = new MediaRecorder(this.mediaStream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 32000,
    });
    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (this.paused) return;
      if (!e.data || e.data.size === 0) return;
      if (this.socket?.readyState !== WebSocket.OPEN) return;
      this.socket.send(e.data);
    };
    this.mediaRecorder.start(250);

    // Start the KeepAlive timer now that the WS is confirmed open and the
    // recorder is running. While the echo guard (pause()) drops audio frames,
    // these JSON text frames keep Deepgram's idle timer from expiring.
    this.keepAliveHandle = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        try { this.socket.send(JSON.stringify({ type: 'KeepAlive' })); }
        catch { /* best effort — socket may be mid-close */ }
      }
    }, this.KEEPALIVE_INTERVAL_MS);

    this.paused = false;
    this.isRunning = true;
    // A fresh session is genuinely up — any earlier disconnect is fully recovered from.
    this.reconnectAttempts = 0;
  }

  stop(): void {
    this.stopRequested = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;

    // Stop the KeepAlive timer first so we don't fire send() after close().
    if (this.keepAliveHandle) {
      clearInterval(this.keepAliveHandle);
      this.keepAliveHandle = null;
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        // Deepgram's documented graceful-close handshake: emit a CloseStream
        // control frame so the server flushes any buffered audio before
        // closing the WS.
        this.socket.send(JSON.stringify({ type: 'CloseStream' }));
      } catch { /* best effort */ }
    }
    this.releaseMediaAndSocket();

    this.paused = false;
    this.isRunning = false;
    // Drop any in-flight utterance — a new call should start with a clean slate.
    this.utteranceBuffer = '';
    this.utteranceConfidences = [];
    this.startOptions = {};
  }

  /**
   * MEET-E-27-02 — stop, but finish the utterance in flight first instead of dropping it. Sends Deepgram's
   * `Finalize` (it flushes the audio it holds into a final Results frame marked `from_finalize`), waits for that
   * frame — at most {@link FINALIZE_WAIT_MS} — then commits whatever `utteranceBuffer` holds as a final on
   * `result$`, and only then tears down. Nothing buffered, nothing extra is emitted. `stop()` still discards.
   */
  async finish(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.paused = true; // no audio after the finish point — only what was already said is flushed
      try {
        this.socket.send(JSON.stringify({ type: 'Finalize' }));
        await new Promise<void>(resolve => {
          const timer = setTimeout(done, DeepgramTranscriptionService.FINALIZE_WAIT_MS);
          function done() { clearTimeout(timer); resolve(); }
          this.finalizeDone = done;
        });
      } catch { /* best effort — commit what is buffered */ }
      this.finalizeDone = null;
    }
    if (this.utteranceBuffer.trim().length > 0) {
      this.commitUtterance();
    }
    this.stop();
  }

  pause(): void { this.paused = true; }
  resume(): void { this.paused = false; }

  // ── helpers ────────────────────────────────────────────────────────────

  /** Releases the recorder/mic/socket without touching stop-vs-reconnect bookkeeping. */
  private releaseMediaAndSocket(): void {
    try { this.mediaRecorder?.stop(); } catch { /* recorder may already be stopped */ }
    this.mediaStream?.getTracks().forEach(t => {
      try { t.stop(); } catch { /* track may already be ended */ }
    });
    try { this.socket?.close(); } catch { /* best effort */ }
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.socket = null;
  }

  /**
   * Reconnect after a close we didn't request. Retries with exponential backoff
   * (1s/2s/4s); after MAX_RECONNECT_ATTEMPTS failures, gives up and emits connectionLost$ so the
   * host can tell the presenter capture needs their attention instead of staying silently dead.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) { return; } // already scheduled

    if (this.reconnectAttempts >= DeepgramTranscriptionService.MAX_RECONNECT_ATTEMPTS) {
      this.reconnectAttempts = 0;
      this.connectionLostSubject.next();
      return;
    }

    this.reconnectAttempts++;
    const delay = DeepgramTranscriptionService.RECONNECT_BASE_DELAY_MS * 2 ** (this.reconnectAttempts - 1);
    console.warn(`[DeepgramTranscription] unexpected close — reconnect attempt `
      + `${this.reconnectAttempts}/${DeepgramTranscriptionService.MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      this.releaseMediaAndSocket(); // clear stale refs from the closed session before retrying
      try {
        await this.start();
      } catch (err) {
        console.warn('[DeepgramTranscription] reconnect attempt failed', err);
        this.scheduleReconnect();
      }
    }, delay);
  }

  /**
   * Parse Deepgram's `Results` and `UtteranceEnd` frames.
   *
   * Frame shapes:
   *   { type:'Results', is_final, speech_final, channel:{ alternatives:[{transcript}] } }
   *   { type:'UtteranceEnd', last_word_end }
   *
   * What each flag means:
   *   - is_final=false  → live partial; will likely change.
   *   - is_final=true   → segment is finalised (Deepgram confident).
   *   - speech_final=true → above PLUS endpointing window of silence elapsed.
   *
   * Why both `speech_final` AND `UtteranceEnd` are needed: for utterances
   * that trail off without crisp trailing silence, `speech_final` may never
   * fire even though the user clearly stopped speaking. The `UtteranceEnd`
   * VAD event is the more reliable boundary in that case. We accumulate
   * `is_final` segments in `utteranceBuffer` and commit on whichever
   * boundary signal arrives first — guaranteeing every utterance commits.
   */
  private handleMessage(evt: MessageEvent): void {
    let payload: any;
    try {
      payload = typeof evt.data === 'string' ? JSON.parse(evt.data) : null;
    } catch (e) {
      console.warn('[DeepgramTranscription] non-JSON frame', e);
      return;
    }
    if (!payload) return;

    // UtteranceEnd — definitive utterance boundary from Deepgram's VAD,
    // even when `speech_final` never fires on the Results stream.
    if (payload.type === 'UtteranceEnd') {
      const committed = this.utteranceBuffer.trim();
      if (committed.length > 0) {
        this.commitUtterance();
      }
      return;
    }

    if (payload.type !== 'Results') return;

    this.handleResults(payload);
    // The answer to a finish()'s Finalize: the flush is in, finish() commits and closes.
    if (payload.from_finalize === true) this.finalizeDone?.();
  }

  private handleResults(payload: any): void {
    const alt = payload.channel?.alternatives?.[0];
    if (!alt) return;
    const text = (alt.transcript ?? '').trim();
    if (!text) return;

    if (payload.is_final === true) {
      // Confirmed segment — append to the running utterance buffer.
      this.utteranceBuffer = (this.utteranceBuffer + ' ' + text).trim();
      if (typeof alt.confidence === 'number' && isFinite(alt.confidence)) {
        this.utteranceConfidences.push(alt.confidence);
      }

      if (payload.speech_final === true) {
        // Silence detected — commit and clear.
        this.commitUtterance();
      } else {
        // Show the accumulated text as interim so the user sees their
        // sentence growing while we wait for the boundary signal.
        this.resultSubject.next({ text: this.utteranceBuffer, isFinal: false });
      }
    } else {
      // Live interim — show buffered finals + current live partial.
      const live = (this.utteranceBuffer + ' ' + text).trim();
      this.resultSubject.next({ text: live, isFinal: false });
    }
  }

  /** Emit the buffered utterance as one final (with its averaged confidence) and clear the buffer. */
  private commitUtterance(): void {
    const confidences = this.utteranceConfidences;
    const confidence = confidences.length
      ? confidences.reduce((sum, c) => sum + c, 0) / confidences.length
      : undefined;
    this.resultSubject.next({ text: this.utteranceBuffer.trim(), isFinal: true, confidence });
    this.utteranceBuffer = '';
    this.utteranceConfidences = [];
  }

  private waitForSocketOpen(socket: WebSocket): Promise<void> {
    return new Promise((resolve, reject) => {
      if (socket.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }
      const onOpen  = () => { cleanup(); resolve(); };
      const onErr   = (e: Event) => { cleanup(); reject(e); };
      const onClose = () => { cleanup(); reject(new Error('socket closed before open')); };
      const cleanup = () => {
        socket.removeEventListener('open',  onOpen);
        socket.removeEventListener('error', onErr);
        socket.removeEventListener('close', onClose);
      };
      socket.addEventListener('open',  onOpen);
      socket.addEventListener('error', onErr);
      socket.addEventListener('close', onClose);
    });
  }

  /**
   * Strip a trailing slash from `Configuration.XatBotApi` (mirrors the
   * MeetingVoiceService helper) so URL concatenation is clean whether the
   * config value ends with / or not.
   */
  private apiBase(): string {
    const api = Configuration.XatBotApi ?? '';
    return api.replace(/\/+$/, '');
  }
}
